
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';

export interface RentalMileageData {
  rentalId: string;
  totalMileage: number;
  dailyMileage: { date: string; mileage: number }[];
  lastUpdated: string | null;
}

export interface MileagePoint {
  id: string;
  latitude: number;
  longitude: number;
  created_at: string;
  rental_mileage: number;
  speed?: number;
}

export const useRentalMileage = (rentalId: string | null) => {
  const [mileageData, setMileageData] = useState<RentalMileageData | null>(null);
  const [mileagePoints, setMileagePoints] = useState<MileagePoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRentalMileage = async () => {
    if (!rentalId) return;

    setLoading(true);
    setError(null);

    try {
      // Получаем общий пробег аренды
      const { data: rentalData, error: rentalError } = await supabase
        .from('rentals')
        .select('total_mileage, start_date, end_date')
        .eq('id', rentalId)
        .single();

      if (rentalError) throw rentalError;

      // Получаем детальные данные о пробеге по точкам используя новое поле rental_id
      const { data: trackingData, error: trackingError } = await supabase
        .from('bike_tracking_history')
        .select('id, latitude, longitude, created_at, rental_mileage, speed')
        .eq('rental_id', rentalId) // Используем прямую связь через rental_id
        .order('created_at', { ascending: true });

      if (trackingError) throw trackingError;

      // Группируем данные по дням для дневной статистики
      const dailyMileage = trackingData.reduce((acc: { [key: string]: number }, point) => {
        const date = new Date(point.created_at).toISOString().split('T')[0];
        acc[date] = (acc[date] || 0) + (point.rental_mileage || 0);
        return acc;
      }, {});

      const dailyMileageArray = Object.entries(dailyMileage).map(([date, mileage]) => ({
        date,
        mileage: Number(mileage.toFixed(2))
      }));

      setMileageData({
        rentalId,
        totalMileage: Number((rentalData.total_mileage || 0).toFixed(2)),
        dailyMileage: dailyMileageArray,
        lastUpdated: trackingData.length > 0 ? trackingData[trackingData.length - 1].created_at : null
      });

      setMileagePoints(trackingData.map(point => ({
        id: point.id,
        latitude: point.latitude,
        longitude: point.longitude,
        created_at: point.created_at,
        rental_mileage: Number((point.rental_mileage || 0).toFixed(3)),
        speed: point.speed
      })));

    } catch (err) {
      console.error('Ошибка при загрузке данных пробега:', err);
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
      toast.error('Не удалось загрузить данные пробега');
    } finally {
      setLoading(false);
    }
  };

  const updateTotalMileage = async () => {
    if (!rentalId) return;

    try {
      // Получаем сумму пробега для данной аренды
      const { data: mileageSum, error: mileageError } = await supabase
        .from('bike_tracking_history')
        .select('rental_mileage')
        .eq('rental_id', rentalId)
        .gt('rental_mileage', 0);

      if (mileageError) throw mileageError;

      const totalMileage = mileageSum.reduce((sum, point) => sum + (point.rental_mileage || 0), 0);

      // Обновляем total_mileage в таблице rentals
      const { data: updateData, error: updateError } = await supabase
        .from('rentals')
        .update({ total_mileage: totalMileage })
        .eq('id', rentalId)
        .select('total_mileage')
        .single();

      if (updateError) throw updateError;

      // Обновляем данные после пересчета
      await fetchRentalMileage();
      
      toast.success('Пробег пересчитан');
      return updateData?.total_mileage || 0;
    } catch (err) {
      console.error('Ошибка при обновлении пробега:', err);
      toast.error('Не удалось пересчитать пробег');
      return null;
    }
  };

  useEffect(() => {
    fetchRentalMileage();
  }, [rentalId]);

  return {
    mileageData,
    mileagePoints,
    loading,
    error,
    refetch: fetchRentalMileage,
    updateTotalMileage
  };
};
